import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useCreateProductAislesPreference, useUpdateProductAislesPreference } from "../../hooks"
import { productAislesPreferenceFormSchema } from "../../types"
import type { ProductAislesPreferenceFormValues } from "../../types"

import ProductAislesPreferenceForm from "./ProductAislesPreferenceForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import { DEFAULT_AUTOCOMPLETE_VALUE } from "@/constants/forms"
import type { ProductAislesPreference } from "@/graphql/codegen/graphql"
import { toStringOrEmpty } from "@/utils/forms"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface ProductAislesPreferenceFormDialogProps {
  productId: number
  aislesPreference: ProductAislesPreference | null
  onSubmit: () => void
  onClose: () => void
}

const ProductAislesPreferenceFormDialog: FC<ProductAislesPreferenceFormDialogProps> = ({
  productId,
  aislesPreference,
  onSubmit,
  onClose,
}) => {
  const isCreateMode = !aislesPreference
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<ProductAislesPreferenceFormValues>({
    defaultValues: {
      warehouse: aislesPreference?.warehouseArea
        ? {
            id: toStringOrEmpty(aislesPreference.warehouseArea.warehouseAreaId),
            label: formatWarehouseLabel(aislesPreference.warehouseArea),
          }
        : DEFAULT_AUTOCOMPLETE_VALUE,
      location: toStringOrEmpty(aislesPreference?.location),
      aisles: aislesPreference?.aisles ?? [],
    },
    resolver: zodResolver(productAislesPreferenceFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
  } = methods
  const { create } = useCreateProductAislesPreference(onSubmit)
  const { update } = useUpdateProductAislesPreference(onSubmit)
  const canSubmit = isDirty && isValid && !isSubmitting

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const submitForm = async ({ warehouse, ...rest }: ProductAislesPreferenceFormValues) => {
    if (isCreateMode) {
      await create({ productId, warehouseAreaId: warehouse.id, ...rest })
    } else {
      await update({
        productAislesPreferenceId: aislesPreference.productAislesPreferenceId,
        warehouseAreaId: warehouse.id,
        ...rest,
      })
    }
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={onClose} />
  ) : (
    <Dialog open fullWidth onClose={handleClose} data-testid='product-aisles-preference-form-dialog'>
      <DialogTitle className='pl-10 font-normal text-primary'>
        {isCreateMode ? "Add" : "Edit"} Preferred Aisles Location
      </DialogTitle>
      <IconButton className='absolute right-4 top-3' aria-label='Close' onClick={handleClose}>
        <CloseOutlined className='text-blue-gray' />
      </IconButton>
      <Divider />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <ProductAislesPreferenceForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button data-testid='submit-button' variant='primary' disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          {isCreateMode ? "Save" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductAislesPreferenceFormDialog
